import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageSquare, X, Send, Trash2, Sparkles, CalendarClock, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function themeFor(pathname) {
  const isShop = pathname.startsWith("/shop");
  return isShop
    ? {
        assistant: "signalhouse",
        name: "Signal House Assistant",
        greeting: "Hey! I'm your Signal House shopping assistant. Looking for a recommendation, or need help with an order?",
        storageKey: "sh_chat_session",
        dark: true,
        accent: "#0a84ff",
        isJames: false,
        starters: [
          { label: "Track my order", type: "nav", value: "/shop/track" },
          { label: "Recommend a gift", type: "send", value: "Can you recommend a good gift?" },
          { label: "Shipping & returns", type: "send", value: "What's your shipping and returns policy?" },
          { label: "Help me choose", type: "send", value: "Help me choose the right product." },
        ],
      }
    : {
        assistant: "james",
        name: "James 1:1 Assistant",
        greeting: "Hi! I can tell you about James 1:1 — music, production services, booking, and more. What would you like to know?",
        storageKey: "james_chat_session",
        dark: false,
        accent: "#c8a96a",
        isJames: true,
        starters: [
          { label: "Book a consultation", type: "lead" },
          { label: "Services & pricing", type: "send", value: "What services does James offer and how much do they cost?" },
          { label: "Mixing & mastering", type: "send", value: "Tell me about your mixing and mastering services." },
          { label: "Hear your music", type: "send", value: "Where can I listen to James 1:1's music?" },
        ],
      };
}

export default function ChatWidget() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // hide on admin routes
  const hidden = pathname.startsWith("/admin");
  const theme = themeFor(pathname);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [cart, setCart] = useState(null);
  const scrollRef = useRef(null);
  const bootedFor = useRef(null);
  const nudgedRef = useRef(false);

  // Reset greeting when switching brand context
  useEffect(() => {
    if (bootedFor.current === theme.assistant) return;
    bootedFor.current = theme.assistant;
    const sid = localStorage.getItem(theme.storageKey);
    setSessionId(sid);
    if (sid) {
      fetch(`${API}/chat/history/${sid}`)
        .then((r) => r.json())
        .then((d) => {
          if (d.messages?.length) setMessages(d.messages.map((m) => ({ role: m.role, content: m.content })));
          else setMessages([{ role: "assistant", content: theme.greeting }]);
        })
        .catch(() => setMessages([{ role: "assistant", content: theme.greeting }]));
    } else {
      setMessages([{ role: "assistant", content: theme.greeting }]);
    }
  }, [theme.assistant, theme.storageKey, theme.greeting]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Signal House: fetch cart + nudge about items left behind (once per open)
  useEffect(() => {
    if (!open || theme.assistant !== "signalhouse") return;
    const cartId = localStorage.getItem("sh_cart_id");
    if (!cartId) return;
    fetch(`${API}/shop/cart/${encodeURIComponent(cartId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        if (!c || !c.total_quantity) return;
        setCart(c);
        if (!nudgedRef.current) {
          nudgedRef.current = true;
          const names = (c.lines || []).map((l) => l.product_title).slice(0, 3).join(", ");
          setMessages((m) => [...m, {
            role: "assistant",
            content: `By the way — you've got ${c.total_quantity} item${c.total_quantity > 1 ? "s" : ""} waiting in your cart${names ? ` (${names})` : ""}. Want to finish checking out? I can help if you have any questions first. 🛒`,
            nudge: true,
          }]);
        }
      })
      .catch(() => {});
  }, [open, theme.assistant, theme.isJames]);

  const cartContext = cart && cart.total_quantity
    ? `The shopper currently has ${cart.total_quantity} item(s) in their cart: ` +
      (cart.lines || []).map((l) => `${l.quantity}x ${l.product_title}${l.variant_title && l.variant_title !== "Default Title" ? ` (${l.variant_title})` : ""}`).join("; ") +
      `. Subtotal ${cart.subtotal ? cart.subtotal.currency + " " + cart.subtotal.amount : ""}. If relevant, gently encourage completing checkout.`
    : null;

  const send = async (e, forcedText) => {
    e?.preventDefault();
    const text = (forcedText != null ? forcedText : input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, assistant: theme.assistant, message: text, context: cartContext }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const lines = part.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).replace(/^ /, "");
          }
          if (event === "session" && data) {
            setSessionId(data);
            localStorage.setItem(theme.storageKey, data);
          } else if (event === "error") {
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry — I hit a snag. Please try again." }; return c; });
          } else if (event === "message") {
            const chunk = data.replace(/\\n/g, "\n");
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + chunk }; return c; });
          }
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Connection error. Please try again." }; return c; });
    } finally {
      setStreaming(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) { try { await fetch(`${API}/chat/history/${sessionId}`, { method: "DELETE" }); } catch (_) { /* noop */ } }
    localStorage.removeItem(theme.storageKey);
    setSessionId(null);
    setShowLead(false);
    setLeadSent(false);
    setMessages([{ role: "assistant", content: theme.greeting }]);
  };

  const handleStarter = (s) => {
    if (s.type === "nav") { setOpen(false); navigate(s.value); }
    else if (s.type === "lead") { setShowLead(true); }
    else { send(null, s.value); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email) return;
    try {
      await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: lead.name, last_name: "", email: lead.email,
          subject: "Chat lead — consultation interest",
          message: `Consultation request captured via the James 1:1 AI assistant (session ${sessionId || "n/a"}).`,
        }),
      });
      setLeadSent(true);
      setMessages((m) => [...m, { role: "assistant", content: `Perfect, ${lead.name.split(" ")[0]} — James will personally reach out to ${lead.email} shortly. Anything else I can help with in the meantime?` }]);
    } catch { /* noop */ }
  };

  if (hidden) return null;

  const isFresh = messages.length <= 1;

  const panelBg = theme.dark ? "bg-[#0e0e10] border-white/10 text-white" : "bg-white border-taupe/40 text-charcoal";
  const headerBg = theme.dark ? "border-white/10" : "border-taupe/30";
  const userBubble = theme.dark ? "bg-[#0a84ff] text-white" : "bg-charcoal text-warmwhite";
  const botBubble = theme.dark ? "bg-white/5 text-white/90" : "bg-taupe/15 text-charcoal";
  const inputBg = theme.dark ? "bg-white/5 border-white/15 text-white placeholder:text-white/30" : "bg-warmwhite border-taupe/50 text-charcoal placeholder:text-charcoal/30";

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open chat assistant"
          data-testid="chat-open-button"
          className="fixed bottom-6 left-6 z-40 flex items-center gap-2.5 pl-3.5 pr-5 py-3.5 rounded-full shadow-lg transition-transform hover:-translate-y-1"
          style={{ backgroundColor: theme.accent, color: theme.dark ? "#fff" : "#1a1a1a" }}
        >
          <MessageSquare className="w-5 h-5" strokeWidth={1.8} />
          <span className="text-[12px] uppercase tracking-widest font-medium hidden sm:inline">Ask AI</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className={`fixed bottom-6 left-6 z-40 w-[calc(100vw-3rem)] sm:w-[380px] h-[560px] max-h-[80vh] border rounded-2xl shadow-2xl flex flex-col overflow-hidden ${panelBg}`}
        >
          <div className={`flex items-center justify-between px-4 h-14 border-b ${headerBg}`}>
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full grid place-items-center" style={{ backgroundColor: `${theme.accent}22` }}>
                <Sparkles className="w-4 h-4" style={{ color: theme.accent }} />
              </span>
              <div>
                <p className="text-sm font-semibold leading-none">{theme.name}</p>
                <p className={`text-[10px] mt-1 ${theme.dark ? "text-white/40" : "text-charcoal/40"}`}>Powered by AI</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearChat} aria-label="Clear conversation" data-testid="chat-clear" className={`p-2 rounded-lg ${theme.dark ? "text-white/50 hover:text-white" : "text-charcoal/50 hover:text-charcoal"}`}>
                <Trash2 className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close" className={`p-2 rounded-lg ${theme.dark ? "text-white/50 hover:text-white" : "text-charcoal/50 hover:text-charcoal"}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-line ${m.role === "user" ? userBubble : botBubble}`}>
                  {m.content || <span className="opacity-50">…</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Starter prompts */}
          {isFresh && !showLead && (
            <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="chat-starters">
              {theme.starters.map((s) => (
                <button
                  key={s.label}
                  onClick={() => handleStarter(s)}
                  data-testid={`chat-starter-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`text-[12px] px-3 py-1.5 rounded-full border transition-colors ${theme.dark ? "border-white/15 text-white/75 hover:border-white/40 hover:text-white" : "border-taupe/50 text-charcoal/70 hover:border-charcoal hover:text-charcoal"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* Lead capture (James 1:1) */}
          {theme.isJames && showLead && !leadSent && (
            <div className={`mx-4 mb-2 p-4 rounded-xl border ${theme.dark ? "border-white/15 bg-white/5" : "border-taupe/50 bg-taupe/10"}`} data-testid="chat-lead-card">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="w-4 h-4" style={{ color: theme.accent }} />
                <p className="text-[13px] font-semibold">Book a consultation</p>
              </div>
              <p className="text-[12px] text-charcoal/60 mb-3">Leave your details and James will personally reach out.</p>
              <form onSubmit={submitLead} className="space-y-2" data-testid="chat-lead-form">
                <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" required className="w-full h-10 px-3 rounded-lg border border-taupe/50 bg-warmwhite text-charcoal text-sm outline-none focus:border-charcoal" data-testid="chat-lead-name" />
                <input type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="you@email.com" required className="w-full h-10 px-3 rounded-lg border border-taupe/50 bg-warmwhite text-charcoal text-sm outline-none focus:border-charcoal" data-testid="chat-lead-email" />
                <div className="flex gap-2">
                  <button type="submit" className="flex-1 h-10 rounded-lg text-white text-[13px] font-medium" style={{ backgroundColor: theme.accent }} data-testid="chat-lead-submit">Request consultation</button>
                  <a href="/#booking" onClick={() => setOpen(false)} className="h-10 px-3 grid place-items-center rounded-lg border border-taupe/50 text-charcoal/70 text-[12px]" data-testid="chat-lead-booking-link">Full form</a>
                </div>
              </form>
            </div>
          )}
          {theme.isJames && leadSent && (
            <div className="mx-4 mb-2 p-3 rounded-xl border border-taupe/40 bg-taupe/10 flex items-center gap-2 text-[13px]" data-testid="chat-lead-success">
              <Check className="w-4 h-4" style={{ color: theme.accent }} /> Request sent — James will be in touch.
            </div>
          )}

          {/* Signal House: resume checkout bar */}
          {!theme.isJames && cart && cart.total_quantity > 0 && cart.checkout_url && (
            <div className="mx-4 mb-2" data-testid="chat-resume-checkout">
              <a
                href={cart.checkout_url}
                className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 transition-colors"
              >
                <span className="text-[13px] text-white/85">Resume checkout · {cart.total_quantity} item{cart.total_quantity > 1 ? "s" : ""}</span>
                <span className="text-[12px] font-medium" style={{ color: theme.accent }}>Checkout →</span>
              </a>
            </div>
          )}

          <form onSubmit={send} className={`border-t ${headerBg} p-3 flex items-center gap-2`} data-testid="chat-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message…"
              className={`flex-1 h-11 px-4 rounded-full border outline-none text-sm ${inputBg}`}
              style={{ ["--tw-ring-color"]: theme.accent }}
              data-testid="chat-input"
            />
            <button type="submit" disabled={streaming || !input.trim()} aria-label="Send" data-testid="chat-send" className="w-11 h-11 rounded-full grid place-items-center shrink-0 disabled:opacity-40 transition-opacity" style={{ backgroundColor: theme.accent, color: theme.dark ? "#fff" : "#1a1a1a" }}>
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
